import { API_ENDPOINTS } from '../config/api';
import { apiFetch } from './api';
import { formatCurrency } from './formatters';
import { getLocalized } from './pooja';

/** Resolve API product/variant names that may be a string or `{en, hi, kn, te}` map. */
export function getProductLocalizedName(value, lang = 'en') {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    return String(getLocalized(value, lang) || '').trim();
  }
  return '';
}

function unwrapProducts(json) {
  const data = json?.data ?? json;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  return [];
}

export async function fetchStoreProducts(lang = 'en') {
  const res = await apiFetch(API_ENDPOINTS.productsForUsers(lang), { skipAuth: true });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success === false) {
    throw new Error(json?.message || 'Failed to load products');
  }
  return unwrapProducts(json).filter((item) => item?.active !== false);
}

export function getProductDisplayPrice(product) {
  const finalPrice = Number(
    product?.finalSellingPrice ??
      product?.final_selling_price ??
      product?.unitPrice ??
      product?.unit_price
  );
  if (Number.isFinite(finalPrice) && finalPrice >= 0) return finalPrice;
  const sellingPrice = Number(
    product?.sellingPrice ?? product?.selling_price ?? product?.price
  );
  return Number.isFinite(sellingPrice) ? sellingPrice : null;
}

export function getProductMrp(product) {
  const selling = Number(product?.sellingPrice);
  const final = getProductDisplayPrice(product);
  if (Number.isFinite(selling) && selling > 0 && final != null && selling > final) {
    return selling;
  }
  return null;
}

export function getProductDiscountPercent(product) {
  const mrp = getProductMrp(product);
  const final = getProductDisplayPrice(product);
  if (mrp == null || final == null || mrp <= final) return null;
  const pct = Math.round(((mrp - final) / mrp) * 100);
  return pct > 0 ? pct : null;
}

export function getProductMaxQuantity(product) {
  if (product?.allowBookingWithoutStock) return 99;
  const stock = Number(
    product?.availableQuantity ?? product?.stockQuantity ?? product?.availableStock
  );
  if (Number.isFinite(stock) && stock > 0) return Math.min(Math.floor(stock), 99);
  // Catalog `quantity` is stock; cart rows also have `quantity` as line count — only
  // treat it as stock when this looks like a catalog product, not a cart line.
  const catalogStock = Number(product?.quantity);
  const looksLikeCartLine = product?.productId != null && product?.product == null;
  if (!looksLikeCartLine && Number.isFinite(catalogStock) && catalogStock > 0) {
    return Math.min(Math.floor(catalogStock), 99);
  }
  return 99;
}

export function isProductOutOfStock(product) {
  if (product?.outOfStock || product?.stockStatus === 'INACTIVE') return true;
  if (!product?.allowBookingWithoutStock) {
    const qty = Number(product?.quantity);
    if (Number.isFinite(qty) && qty <= 0) return true;
  }
  return false;
}

export function isVariantAvailable(product) {
  return !isProductOutOfStock(product);
}

/** Unique gallery URLs: thumbnail + multipleImages (string or {url|imageUrl|...}). */
export function getProductGalleryImages(product) {
  const seen = new Set();
  const out = [];
  const push = (raw) => {
    const url = String(raw || '').trim();
    if (!url || seen.has(url)) return;
    seen.add(url);
    out.push(url);
  };

  push(product?.thumbnailImage || product?.thumbnail_image || product?.imageUrl);

  const multi = product?.multipleImages ?? product?.multiple_images ?? [];
  if (Array.isArray(multi)) {
    for (const item of multi) {
      if (typeof item === 'string') push(item);
      else if (item && typeof item === 'object') {
        push(item.url || item.imageUrl || item.image || item.thumbnailImage || item.src);
      }
    }
  }

  return out;
}

export function findStoreProductGroup(products, productId) {
  const id = String(productId || '').trim();
  if (!id || !Array.isArray(products)) return null;
  const match = products.find((p) => String(p?.id) === id);
  if (!match) return null;
  const groups = groupStoreProducts(products);
  const key = getProductGroupKey(match);
  return groups.find((g) => g.key === key) || null;
}

export function getProductGroupKey(product) {
  if (product?.parentProductId) return String(product.parentProductId);
  const name = getProductLocalizedName(product?.productName).toLowerCase();
  const cat = product?.categoryId || '';
  const sub = product?.subCategoryId || '';
  if (name) return `name:${name}|${cat}|${sub}`;
  return String(product?.id || '');
}

export function hasProductVariantPicker(variants = []) {
  return variants.length > 1;
}

export function getProductNameSuffix(productName, lang = 'en') {
  const raw = getProductLocalizedName(productName, lang);
  const pipe = raw.indexOf(' | ');
  if (pipe < 0) return '';
  return raw.slice(pipe + 3).trim();
}

export function getProductUnitLabel(product) {
  const value = product?.unitValue;
  const unitName = (product?.unitName || '').trim();
  const unitCode = (product?.unitCode || '').trim();
  const unit = unitName || unitCode;
  if (value != null && String(value).trim() !== '' && unit) {
    const num = Number(value);
    const formatted = Number.isFinite(num)
      ? Number.isInteger(num)
        ? String(num)
        : String(num)
      : String(value).trim();
    const unitLabel = unitName || (unitCode === 'PCS' ? 'Pcs' : unitCode);
    return `${formatted} ${unitLabel}`.trim();
  }
  if (unitName) return unitName;
  if (unitCode === 'PCS') return 'Pcs';
  return unitCode;
}

function truncateLabel(text, max = 48) {
  const raw = (text || '').trim();
  if (!raw) return '';
  if (raw.length <= max) return raw;
  return `${raw.slice(0, max - 1).trim()}…`;
}

/** Best available label for a SKU — variant name, unit, description, SKU, etc. */
export function getVariantLabel(product, index = 0, lang = 'en') {
  const name = getProductLocalizedName(product?.variantName, lang);
  if (name) return name;

  const unit = getProductUnitLabel(product);
  if (unit) return unit;

  const shortDesc = truncateLabel(
    stripHtml(getProductLocalizedName(product?.shortDescription, lang))
  );
  if (shortDesc) return shortDesc;

  const sku = (product?.skuCode || '').trim();
  if (sku) return sku;

  const nameSuffix = truncateLabel(getProductNameSuffix(product?.productName, lang));
  if (nameSuffix) return nameSuffix;

  const subCategory = getProductLocalizedName(product?.subCategoryName, lang);
  if (subCategory) return subCategory;

  const price = getProductDisplayPrice(product);
  if (price != null) return formatCurrency(price);

  return `Option ${index + 1}`;
}

export function getVariantLabelForDisplay(product, index = 0, lang = 'en') {
  const label = getVariantLabel(product, index, lang);
  if (/^Option \d+$/.test(label)) return '';
  const price = getProductDisplayPrice(product);
  if (price != null && label === formatCurrency(price)) return '';
  return label;
}

/** Group API rows that share a product name (or parent) into one card with variants. */
export function groupStoreProducts(products) {
  const groups = new Map();

  for (const product of products) {
    const key = getProductGroupKey(product);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        productName: product?.productName || '',
        categoryName: getProductLocalizedName(product?.categoryName) || product?.categoryName || '',
        subCategoryName:
          getProductLocalizedName(product?.subCategoryName) || product?.subCategoryName || '',
        thumbnailImage: product?.thumbnailImage || '',
        variants: [],
      });
    }
    groups.get(key).variants.push(product);
  }

  return Array.from(groups.values()).map((group) => {
    const variants = [...group.variants].sort((a, b) => {
      const byName = getVariantLabel(a).localeCompare(getVariantLabel(b));
      if (byName !== 0) return byName;
      return (getProductDisplayPrice(a) || 0) - (getProductDisplayPrice(b) || 0);
    });
    const withImage = variants.find((v) => v?.thumbnailImage);
    return {
      ...group,
      variants,
      thumbnailImage: withImage?.thumbnailImage || group.thumbnailImage,
    };
  });
}

export function getDefaultVariant(variants = []) {
  if (!variants.length) return null;
  return variants[0];
}

export function getGroupFromPrice(variants = []) {
  const prices = variants
    .map((v) => getProductDisplayPrice(v))
    .filter((p) => p != null && Number.isFinite(p));
  if (!prices.length) return null;
  return Math.min(...prices);
}

export function snapshotStoreProduct(product, lang = 'en') {
  if (!product?.id) return null;
  const variantLabel =
    getVariantLabelForDisplay(product, 0, lang) ||
    getProductLocalizedName(product.variantName, lang);
  return {
    id: product.id,
    productName: getProductLocalizedName(product.productName, lang),
    variantName: variantLabel,
    thumbnailImage: product.thumbnailImage || '',
    finalSellingPrice:
      product.finalSellingPrice ?? product.final_selling_price ?? product.unitPrice ?? product.unit_price,
    sellingPrice: product.sellingPrice ?? product.selling_price ?? product.price,
    outOfStock: isProductOutOfStock(product),
    maxQuantity: 99,
  };
}

export function stripHtml(html) {
  if (!html || typeof html !== 'string') return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}
